import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';


import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import { OperationalPeriodScheduleControl } from '../OperationalPeriodScheduleControl';
import {
  useExportIcs215,
  useIcs215Tip,
  useIcs215Versions,
  type Ics215AssignmentSnapshot,
} from 'components/IAP/hooks/ics215FormVersionHooks';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormFamily } from 'components/IAP/hooks/useIcsFormFamily';
import { useIncidentPermissions } from 'components/IAP/hooks/incidentHooks';
import {
  useCreateWorkAssignment,
  useUpdateWorkAssignment,
  useWorkAssignments,
  type WorkAssignment,
} from 'components/IAP/hooks/workAssignmentHooks';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import { useGetDraftRosterMembersForIncident } from 'components/IAP/hooks/incidentRoster/useGetDraftRosterMembersForIncident';
import { incidentCardOperationalPlanningWorksheetTitle } from '../../USCGPage';
import { useICSContext } from '../../ICSContext';
import {
  buildIcs215ResourceColumns,
  upsertResourceList,
} from './buildIcs215ResourceColumns';
import {
  assignmentKey,
  buildBaselineByKey,
  cloneAssignments,
  dirtyAssignmentKeys,
  serializeAssignment,
} from './draftUtils';
import { Ics215LegacyWorkAssignmentsTable } from './Ics215LegacyWorkAssignmentsTable';
import { ICS215_OUTLINE_BTN } from './ics215Styles';
import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from '../icsBoxPrimitives';
import {
  ICS215_ASSIGNMENT_GRID_HINT,
  ICS215_ASSIGNMENT_GRID_LABEL,
  ICS215_BOX_LABELS,
  ICS215_DATE_PREPARED_HINT,
  ICS215_INCIDENT_LOCATION_HINT,
  ICS215_INCIDENT_NAME_HINT,
  ICS215_OPERATIONAL_PERIOD_HINT,
} from './ics215BoxLabels';
import Ics215VersioningPanel from './Ics215VersioningPanel';
import {
  getIcs215SignatureCompletion,
  isIcs215VersionLocked,
  parseWalaSignatures,
} from './ics215SignatureSlots';
import type { WalaVersionRow } from '../AssignmentList/Ics204VersionsDialog';

const byNewest = (
  a: { timestamp: Date | string },
  b: { timestamp: Date | string }
) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();

function snapshotToWorkAssignment(
  row: Ics215AssignmentSnapshot,
  periodNumber: number
): WorkAssignment {
  return {
    id: row.workAssignmentId,
    work_assignment_id: row.workAssignmentId,
    incident_id: '',
    user_guid: '',
    version: 1,
    work_assignment: row.workAssignment,
    overhead_positions: row.overheadPositions,
    reporting_location: row.reportingLocation,
    requested_arrival_time: row.requestedArrivalTime
      ? (new Date(row.requestedArrivalTime) as unknown as Date)
      : undefined,
    special_instructions:
      row.specialEquipmentSupplies || row.specialInstructions,
    timestamp: new Date(),
    archived: false,
    geolocation: null,
    strike_teams: [],
    resource_sets: [],
    leader: '',
    leader_contact: '',
    status: 'Planned',
    work_group_name: row.workGroupName,
    resource_list: (row.resourceList ?? []).map((item) => ({
      resourceName: item.resourceName,
      required: Number(item.required) || 0,
      have: Number(item.have) || 0,
      need: Number(item.need) || 0,
    })),
    operational_period_number: periodNumber,
  } as WorkAssignment;
}

const Ics215StandaloneWorksheet: FC = () => {
  const { data: workAssignments } = useWorkAssignments();
  const { data: roster } = useGetPositionsForIncident();
  const { data: draftRoster } = useGetDraftRosterMembersForIncident();
  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  const {
    effectivePeriodNumber: periodNumber,
    isFuture,
    readOnlyForms,
    isWorking,
  } = useICSContext();
  const { isMapVisible } = useUscgMapContext();
  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const { exportAsForm, previewExportedForm } = useIcsFormPdfPreview();
  const formFamily = useIcsFormFamily();
  const exportIcs215 = useExportIcs215();

  const createWorkAssignment = useCreateWorkAssignment();
  const updateWorkAssignment = useUpdateWorkAssignment();
  const { data: tip } = useIcs215Tip(periodNumber);
  const { data: versions = [] } = useIcs215Versions(tip?.ics215Id ?? '');

  const [draftAssignments, setDraftAssignments] = useState<WorkAssignment[]>(
    []
  );
  /**
   * Last-saved serialization per row. Rows are saved one at a time, so a single whole-grid snapshot cannot say which
   * row is pending.
   */
  const [baselineByKey, setBaselineByKey] = useState<Record<string, string>>(
    {}
  );
  const [extraResourceColumns, setExtraResourceColumns] = useState<string[]>(
    []
  );
  const [focusAssignmentId, setFocusAssignmentId] = useState<string | null>(
    null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [savingRowKey, setSavingRowKey] = useState<string | null>(null);
  const [isCreatingSignedVersion, setIsCreatingSignedVersion] = useState(false);
  const [viewingVersionId, setViewingVersionId] = useState<string | null>(null);

  const tipSignatures = useMemo(
    () => parseWalaSignatures(tip?.signatures),
    [tip?.signatures]
  );
  const tipLocked = isIcs215VersionLocked(tipSignatures);
  const viewingVersion = useMemo(
    () =>
      viewingVersionId
        ? versions.find((version) => version.id === viewingVersionId)
        : undefined,
    [viewingVersionId, versions]
  );

  const gridReadOnly =
    readOnlyForms ||
    !canEditCards ||
    !!viewingVersionId ||
    (tipLocked && !isCreatingSignedVersion);

  const canEdit = !gridReadOnly;

  const serverAssignments = useMemo(
    () =>
      (workAssignments ?? [])
        .filter(
          (work) =>
            !work.archived && work.operational_period_number === periodNumber
        )
        .slice()
        .sort(byNewest),
    [workAssignments, periodNumber]
  );

  const serverByKey = useMemo(() => {
    const map = new Map<string, WorkAssignment>();
    for (const work of workAssignments ?? []) {
      map.set(assignmentKey(work), work);
    }
    return map;
  }, [workAssignments]);

  const dirtyRowKeys = useMemo(
    () => dirtyAssignmentKeys(draftAssignments, baselineByKey),
    [draftAssignments, baselineByKey]
  );

  /*
   * Added columns are presentation only: `handleAddResourceColumn` never touches `draftAssignments`, so a bare column
   * has nothing to persist. Values typed into it dirty the row that owns them.
   */
  const isDirty = dirtyRowKeys.size > 0;

  const draftRef = useRef(draftAssignments);
  const baselineRef = useRef(baselineByKey);
  draftRef.current = draftAssignments;
  baselineRef.current = baselineByKey;

  useEffect(() => {
    if (viewingVersion) {
      const payload = viewingVersion.payload;
      const next = cloneAssignments(
        (payload?.assignments ?? []).map((row) =>
          snapshotToWorkAssignment(row, periodNumber)
        )
      );
      setDraftAssignments(next);
      setExtraResourceColumns(payload?.resourceKinds ?? []);
      setBaselineByKey(buildBaselineByKey(next));
      return;
    }

    // Locked tip (not creating a new version): show frozen snapshot.
    if (tipLocked && !isCreatingSignedVersion && tip?.payload) {
      const next = cloneAssignments(
        (tip.payload.assignments ?? []).map((row) =>
          snapshotToWorkAssignment(row, periodNumber)
        )
      );
      setDraftAssignments(next);
      setExtraResourceColumns(tip.payload.resourceKinds ?? []);
      setBaselineByKey(buildBaselineByKey(next));
      return;
    }

    // Creating signed from a locked tip: seed once from tip, then allow live edits.
    if (
      isCreatingSignedVersion &&
      tipLocked &&
      tip?.payload?.assignments?.length
    ) {
      const dirtyKeys = dirtyAssignmentKeys(
        draftRef.current,
        baselineRef.current
      );
      if (dirtyKeys.size === 0) {
        const next = cloneAssignments(
          tip.payload.assignments.map((row) =>
            snapshotToWorkAssignment(row, periodNumber)
          )
        );
        setDraftAssignments(next);
        setExtraResourceColumns(tip.payload.resourceKinds ?? []);
        setBaselineByKey(buildBaselineByKey(next));
        return;
      }
    }

    /*
     * Per-row baselines let the server refresh every clean row while holding back only the ones being edited. A single
     * unsaved cell used to freeze the whole grid.
     */
    const dirtyKeys = dirtyAssignmentKeys(draftRef.current, baselineRef.current);
    const serverRows = cloneAssignments(serverAssignments);

    if (dirtyKeys.size > 0) {
      const draftByKey = new Map(
        draftRef.current.map((work) => [assignmentKey(work), work])
      );
      const merged = serverRows.map((work) => {
        const key = assignmentKey(work);
        return dirtyKeys.has(key) ? (draftByKey.get(key) ?? work) : work;
      });

      // A dirty row the server no longer returns is still being edited, so it stays until saved or cancelled.
      const serverKeys = new Set(serverRows.map(assignmentKey));
      const orphanedDirty = draftRef.current.filter(
        (work) =>
          dirtyKeys.has(assignmentKey(work)) &&
          !serverKeys.has(assignmentKey(work))
      );

      const next = [...orphanedDirty, ...merged];
      setDraftAssignments(next);
      setBaselineByKey((prev) => {
        const baseline = buildBaselineByKey(next);
        // Dirty rows keep the baseline they were last clean at, so they stay marked dirty.
        for (const key of dirtyKeys) {
          if (prev[key] !== undefined) baseline[key] = prev[key];
          else delete baseline[key];
        }
        return baseline;
      });
      return;
    }

    setDraftAssignments(serverRows);
    setExtraResourceColumns([]);
    setBaselineByKey(buildBaselineByKey(serverRows));
  }, [
    serverAssignments,
    viewingVersion,
    tipLocked,
    isCreatingSignedVersion,
    tip?.payload,
    periodNumber,
  ]);

  const visibleDraftAssignments = useMemo(
    () => draftAssignments.filter((work) => !work.archived),
    [draftAssignments]
  );

  const workGroups = useMemo(
    () =>
      roster
        ?.filter((member) => member.is_workgroup && !member.isResourcePosition)
        .map((wg) => ({
          id: wg.id,
          name: wg.name,
          hasDraftLeader:
            draftRoster?.some((m) =>
              m.incidentPositions?.some(
                (p) => p.positionId === wg.id && p.isLeader
              )
            ) ?? false,
        })) || [],
    [roster, draftRoster]
  );

  const resourceColumns = useMemo(
    () =>
      buildIcs215ResourceColumns(visibleDraftAssignments, extraResourceColumns),
    [visibleDraftAssignments, extraResourceColumns]
  );

  const patchDraft = useCallback(
    (work: WorkAssignment, patch: Partial<WorkAssignment>) => {
      const key = assignmentKey(work);
      setDraftAssignments((prev) =>
        prev.map((row) =>
          assignmentKey(row) === key ? { ...row, ...patch } : row
        )
      );
    },
    []
  );

  const handlePatchAssignment = useCallback(
    (work: WorkAssignment, patch: Partial<WorkAssignment>) => {
      if (!canEdit) return;
      patchDraft(work, patch);
    },
    [canEdit, patchDraft]
  );

  const handlePatchResource = useCallback(
    (
      work: WorkAssignment,
      resourceName: string,
      field: 'required' | 'have' | 'need',
      value: string
    ) => {
      if (!canEdit) return;
      const resource_list = upsertResourceList(
        work.resource_list,
        resourceName,
        field,
        value
      );
      patchDraft(work, { resource_list });
    },
    [canEdit, patchDraft]
  );

  /** Writes one row and returns the payload sent, so callers can refresh that row's baseline. */
  const persistAssignment = useCallback(
    async (draft: WorkAssignment, overrides?: Partial<WorkAssignment>) => {
      const key = assignmentKey(draft);
      const server = serverByKey.get(key);
      await updateWorkAssignment.mutateAsync({
        ...(server ?? draft),
        ...draft,
        ...overrides,
        id: key,
        operational_period_number: periodNumber,
      });
    },
    [periodNumber, serverByKey, updateWorkAssignment]
  );

  /** Saves the given rows and marks each one clean. */
  const commitRows = useCallback(
    async (rows: WorkAssignment[]) => {
      if (rows.length === 0) return;
      await Promise.all(rows.map((row) => persistAssignment(row)));
      setBaselineByKey((prev) => {
        const next = { ...prev };
        for (const row of rows) {
          next[assignmentKey(row)] = serializeAssignment(row);
        }
        return next;
      });
    },
    [persistAssignment]
  );

  const handleSaveAssignment = useCallback(
    async (work: WorkAssignment) => {
      if (!canEdit) return;
      const key = assignmentKey(work);
      const draft = draftRef.current.find((row) => assignmentKey(row) === key);
      if (!draft) return;
      setSavingRowKey(key);
      try {
        await commitRows([draft]);
      } finally {
        setSavingRowKey(null);
      }
    },
    [canEdit, commitRows]
  );

  const handleCancelAssignment = useCallback(
    (work: WorkAssignment) => {
      if (!canEdit) return;
      const key = assignmentKey(work);
      const server = serverByKey.get(key);

      /*
       * Reverting means going back to what the server holds. Without a server row there is nothing to revert to, so
       * the row leaves the grid rather than sitting there with a cancel button that does nothing.
       */
      if (!server) {
        setDraftAssignments((prev) =>
          prev.filter((row) => assignmentKey(row) !== key)
        );
        setBaselineByKey((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }

      const restored = cloneAssignments([server])[0]!;
      setDraftAssignments((prev) =>
        prev.map((row) => (assignmentKey(row) === key ? restored : row))
      );
      setBaselineByKey((prev) => ({
        ...prev,
        [key]: serializeAssignment(restored),
      }));
    },
    [canEdit, serverByKey]
  );

  const handleDeleteAssignment = useCallback(
    (work: WorkAssignment) => {
      if (!canEdit) return;
      const key = assignmentKey(work);
      // Committed straight away: there is no longer a footer Save to carry a pending archive.
      void persistAssignment(work, { archived: true });
      setDraftAssignments((prev) =>
        prev.filter((row) => assignmentKey(row) !== key)
      );
      setBaselineByKey((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [canEdit, persistAssignment]
  );

  const handleRenameResourceColumn = useCallback(
    (fromName: string, toName: string) => {
      if (!canEdit) return;
      const trimmed = toName.trim();
      if (!trimmed || trimmed === fromName) return;

      setExtraResourceColumns((prev) => {
        const withoutFrom = prev.filter((name) => name !== fromName);
        if (withoutFrom.includes(trimmed)) return withoutFrom;
        if (prev.includes(fromName)) {
          return prev.map((name) => (name === fromName ? trimmed : name));
        }
        return [...withoutFrom, trimmed];
      });

      /*
       * A rename rewrites `resource_list` on every row that used the old name. Persisting them here keeps the rename a
       * worksheet-level action; leaving it to the per-row buttons would mark the whole grid dirty and make the user
       * save each row by hand.
       */
      const rewritten: WorkAssignment[] = [];
      const next = draftRef.current.map((work) => {
        const list = work.resource_list;
        if (!list?.some((item) => item.resourceName === fromName)) {
          return work;
        }
        const updated = {
          ...work,
          resource_list: list.map((item) =>
            item.resourceName === fromName
              ? { ...item, resourceName: trimmed }
              : item
          ),
        };
        rewritten.push(updated);
        return updated;
      });

      setDraftAssignments(next);
      void commitRows(rewritten);
    },
    [canEdit, commitRows]
  );

  const handleDeleteResourceColumn = useCallback(
    (resourceName: string) => {
      if (!canEdit) return;
      setExtraResourceColumns((prev) =>
        prev.filter((name) => name !== resourceName)
      );

      // Same reasoning as the rename: a column drop touches many rows and commits on its own.
      const rewritten: WorkAssignment[] = [];
      const next = draftRef.current.map((work) => {
        if (
          !work.resource_list?.some(
            (item) => item.resourceName === resourceName
          )
        ) {
          return work;
        }
        const updated = {
          ...work,
          resource_list: (work.resource_list ?? []).filter(
            (item) => item.resourceName !== resourceName
          ),
        };
        rewritten.push(updated);
        return updated;
      });

      setDraftAssignments(next);
      void commitRows(rewritten);
    },
    [canEdit, commitRows]
  );

  const handleAddResourceColumn = useCallback(
    (name: string) => {
      if (!canEdit) return;
      const trimmed = name.trim();
      if (!trimmed) return;
      setExtraResourceColumns((prev) => {
        if (prev.includes(trimmed)) return prev;
        if (resourceColumns.some((col) => col.label === trimmed)) return prev;
        return [...prev, trimmed];
      });
    },
    [canEdit, resourceColumns]
  );

  /**
   * Flushes every pending row at once. Rows are otherwise saved individually; this exists so signing cannot publish a
   * worksheet that still has edits sitting in the grid.
   */
  const saveAllDirtyRows = async () => {
    setIsSaving(true);
    try {
      await commitRows(
        draftAssignments.filter((draft) =>
          dirtyRowKeys.has(assignmentKey(draft))
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddWorkAssignment = async () => {
    if (!canEdit) return;
    const { data } = await createWorkAssignment.mutateAsync({
      work_assignment: 'New Work Assignment',
      status: 'Planned',
      parent_tactics: [],
      operational_period_number: periodNumber,
    });
    const entity = (data ?? {}) as WorkAssignment & {
      workAssignmentId?: string;
    };
    const focusId =
      entity.work_assignment_id || entity.workAssignmentId || entity.id;
    if (entity && focusId) {
      const created: WorkAssignment = {
        ...entity,
        work_assignment_id: focusId,
        work_assignment: entity.work_assignment || 'New Work Assignment',
        status: entity.status || 'Planned',
        archived: false,
        resource_list: entity.resource_list ?? [],
        operational_period_number: periodNumber,
      } as WorkAssignment;
      setDraftAssignments((prev) => {
        if (prev.some((row) => assignmentKey(row) === focusId)) return prev;
        return cloneAssignments([created, ...prev]);
      });
      // The row is already persisted by the call above, so it starts clean and shows no save/cancel until edited.
      setBaselineByKey((prev) => ({
        ...prev,
        [focusId]: serializeAssignment(created),
      }));
      setFocusAssignmentId(focusId);
    }
  };

  /**
   * Box 3. A signed worksheet was prepared when it was signed, matching what
   * both exports print; an unsigned one has not been prepared yet.
   */
  const datePrepared = tipSignatures[0]
    ? dayjs(tipSignatures[0].signedAt).format('MM/DD/YYYY HH:mm')
    : '';

  /**
   * PDF renders from the incident's live worksheet, but Word is built from the
   * version tip, so the two formats take different routes to the same preview.
   */
  const handleExport = async (format: 'pdf' | 'docx') => {
    // FEMA Word is the NIMS template, filled by exportAsForm. The version route
    // below builds the Coast Guard worksheet.
    if (format === 'docx' && formFamily === 'fema') {
      await exportAsForm({
        formname: 'ICS_215_CG',
        data: {},
        mapSketch: null,
        format: 'docx',
      });
      return;
    }
    if (format === 'pdf') {
      await exportAsForm({
        formname: 'ICS_215_CG',
        data: {},
        mapSketch: null,
        format: 'pdf',
      });
      return;
    }

    if (!tip?.ics215Id) {
      toast.error(
        'Word export needs a saved worksheet version. Save the worksheet, then try again.'
      );
      return;
    }

    const result = await exportIcs215.mutateAsync({
      ics215Id: tip.ics215Id,
      skipWorkspaceUpload: true,
    });
    const docxBase64 = typeof result === 'string' ? result : result.docxBase64;
    if (docxBase64) {
      previewExportedForm({
        base64: docxBase64,
        formname: 'ICS_215_CG',
        format: 'docx',
        periodNumber,
      });
    }
  };

  const exportActions = (
    <FormExportButton
      formname="ICS_215_CG"
      label="Preview ICS-215"
      disabled={exportIcs215.isPending}
      onExport={(format) => {
        void handleExport(format);
      }}
    />
  );

  const body = (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <div className="tw:grid tw:gap-3 tw:md:grid-cols-4">
        <Ics204CardSection
          boxLabel={ICS215_BOX_LABELS.incidentName}
          testId="ics215-box-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS215_BOX_LABELS.incidentName}>
              {ICS215_INCIDENT_NAME_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incident.data?.name ?? ''}
            testId="ics215-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS215_BOX_LABELS.incidentLocation}
          testId="ics215-box-incident-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS215_BOX_LABELS.incidentLocation}>
              {ICS215_INCIDENT_LOCATION_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={
              incidentInfo?.incidentLocation || incident.data?.address || ''
            }
            testId="ics215-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS215_BOX_LABELS.datePrepared}
          testId="ics215-box-date-prepared"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS215_BOX_LABELS.datePrepared}>
              {ICS215_DATE_PREPARED_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue value={datePrepared} testId="ics215-date-prepared" />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS215_BOX_LABELS.operationalPeriod}
          testId="ics215-box-operational-period"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS215_BOX_LABELS.operationalPeriod}>
              {ICS215_OPERATIONAL_PERIOD_HINT}
            </BoxInfo>
          }
        >
          <OperationalPeriodScheduleControl testId="ics215-operational-period" />
        </Ics204CardSection>
      </div>

      <Ics215VersioningPanel
        ics215Id={tip?.ics215Id ?? null}
        periodNumber={periodNumber}
        tipId={tip?.id}
        tipSignatures={tipSignatures}
        tipUpdatedAt={tip?.updatedAt}
        tipUpdatedByUserName={tip?.updatedByUserName}
        isCreatingSignedVersion={isCreatingSignedVersion}
        viewingVersionId={viewingVersionId}
        readOnly={readOnlyForms || !canEditCards || !isWorking}
        onStartCreateSignedVersion={() => {
          setViewingVersionId(null);
          setIsCreatingSignedVersion(true);
        }}
        onCancelCreateSignedVersion={() => setIsCreatingSignedVersion(false)}
        onSignedVersionCreated={() => {
          setIsCreatingSignedVersion(false);
          setViewingVersionId(null);
        }}
        onUnsignedVersionCreated={() => {
          setIsCreatingSignedVersion(false);
          setViewingVersionId(null);
        }}
        onViewVersion={(version: WalaVersionRow) => {
          setIsCreatingSignedVersion(false);
          setViewingVersionId(version.id);
        }}
        onViewLatest={() => setViewingVersionId(null)}
        onBeforeSign={async () => {
          if (isDirty) {
            await saveAllDirtyRows();
          }
        }}
      >
        <Ics204CardSection
          boxLabel={ICS215_ASSIGNMENT_GRID_LABEL}
          testId="ics215-box-assignment-grid"
          className="tw:mt-0 tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS215_ASSIGNMENT_GRID_LABEL}>
              {ICS215_ASSIGNMENT_GRID_HINT}
            </BoxInfo>
          }
        >
          <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
            {canEdit ? (
              <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={ICS215_OUTLINE_BTN}
                  onClick={() => {
                    void handleAddWorkAssignment();
                  }}
                >
                  + Add Work Assignment
                </Button>
              </div>
            ) : null}
            <Ics215LegacyWorkAssignmentsTable
              assignments={visibleDraftAssignments}
              resourceColumns={resourceColumns}
              workGroups={workGroups}
              canEdit={canEdit}
              focusAssignmentId={focusAssignmentId}
              onFocusConsumed={() => setFocusAssignmentId(null)}
              onPatchAssignment={handlePatchAssignment}
              onPatchResource={handlePatchResource}
              onDeleteAssignment={handleDeleteAssignment}
              dirtyRowKeys={dirtyRowKeys}
              savingRowKey={savingRowKey}
              onSaveAssignment={handleSaveAssignment}
              onCancelAssignment={handleCancelAssignment}
              onRenameResourceColumn={handleRenameResourceColumn}
              onDeleteResourceColumn={handleDeleteResourceColumn}
              onAddResourceColumn={handleAddResourceColumn}
            />
          </div>
        </Ics204CardSection>
      </Ics215VersioningPanel>

      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:text-xs tw:text-muted-foreground">
          {visibleDraftAssignments.length} Work Assignment
          {visibleDraftAssignments.length === 1 ? '' : 's'}
          {tipLocked && !isCreatingSignedVersion && !viewingVersionId
            ? ' · Signed tip (read-only)'
            : ''}
          {getIcs215SignatureCompletion(tipSignatures).status === 'draft' &&
          isWorking
            ? ' · Live worksheet'
            : ''}
        </div>
      </div>

    </div>
  );

  // Beside the map on a future period there is no room for card chrome, so the
  // boxes stand on the panel itself, as the 202 does.
  if (isMapVisible && isFuture) {
    return (
      <div
        className="dark content tw:text-foreground mt-2"
        data-tour="ics215-card"
      >
        {body}
      </div>
    );
  }

  return (
    <Card
      data-testid="uscg-215-card"
      data-tour="ics215-card"
      data-form-family="uscg"
      className="dark tw:bg-background tw:text-foreground"
    >
      <CardHeader>
        <CardTitle>{incidentCardOperationalPlanningWorksheetTitle}</CardTitle>
        <CardAction>{exportActions}</CardAction>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

export default Ics215StandaloneWorksheet;
